import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import {
  CreditCard,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Zap,
  Mail,
  FileText,
  Mic,
} from 'lucide-react'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'

// ─── types ────────────────────────────────────────────────────────────────────

interface CreditTransaction {
  id: string | number
  description: string
  amount: number
  created_at: string
}

interface CreditDashboard {
  pool: { available_credits?: number }
  quota_status: {
    plan_name: string
    usage: { esignature?: number; email?: number; ai_tokens?: number }
    quotas: { max_signatures?: number; max_emails?: number; max_ai_tokens?: number }
    warnings?: string[]
  } | null
  transactions: CreditTransaction[]
  threshold_20pct?: number
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function pctBar(used: number, max: number): number {
  if (max === 0) return 0
  return Math.min(100, Math.round((used / max) * 100))
}

// ─── QuotaBar ─────────────────────────────────────────────────────────────────

function QuotaBar({
  label,
  used,
  max,
  icon,
  unlimited,
}: {
  label: string
  used: number
  max: number
  icon: React.ReactNode
  unlimited: string
}) {
  if (max === 0) {
    return (
      <div className="flex items-center justify-between py-2 text-sm">
        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          {icon}
          {label}
        </span>
        <span className="text-green-600 font-medium">{unlimited}</span>
      </div>
    )
  }
  const pct = pctBar(used, max)
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-400' : 'bg-blue-500'
  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          {icon}
          {label}
        </span>
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {used.toLocaleString()} / {max.toLocaleString()} ({pct}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
        <div
          className={`${color} h-1.5 rounded-full transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ─── PaymentForm (Stripe Elements) ────────────────────────────────────────────

function PaymentFormInner({
  amountHT,
  vatRate,
  amountTTC,
  onSuccess,
  onCancel,
}: {
  amountHT: number
  vatRate: number
  amountTTC: number
  onSuccess: (newBalance: number) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [unrecoverable, setUnrecoverable] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setLoading(true)
    setError('')
    setUnrecoverable(false)

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    })

    if (stripeError) {
      setError(stripeError.message ?? t('mesCredits.paymentError'))
      const unrecoverableCodes = [
        'payment_intent_unexpected_state',
        'payment_intent_incompatible_payment_method',
        'payment_method_not_available',
      ]
      if (stripeError.code && unrecoverableCodes.includes(stripeError.code)) {
        setUnrecoverable(true)
      }
      setLoading(false)
      return
    }

    try {
      const result = await api.confirmCreditsPurchase(paymentIntent?.id ?? '')
      onSuccess(result.new_balance)
    } catch {
      setError(t('mesCredits.creditError'))
    }
    setLoading(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* HT / TVA / TTC breakdown */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300 space-y-0.5">
        <div className="flex justify-between">
          <span>{t('mesCredits.amountHT')}</span>
          <span>{amountHT.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
        </div>
        <div className="flex justify-between">
          <span>{t('mesCredits.vat', { rate: vatRate })}</span>
          <span>
            {(amountTTC - amountHT).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
          </span>
        </div>
        <div className="flex justify-between font-bold text-sm border-t border-blue-200 dark:border-blue-700 pt-1 mt-1 text-blue-900 dark:text-blue-100">
          <span>{t('mesCredits.totalTTC')}</span>
          <span>{amountTTC.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
        </div>
      </div>
      <PaymentElement />
      {error && (
        <div className="space-y-2">
          <p className="text-sm text-red-600 dark:text-red-400">{t(error)}</p>
          {unrecoverable && (
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-blue-600 dark:text-blue-400 underline"
            >
              {t('mesCredits.retryPayment')}
            </button>
          )}
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={!stripe || loading || unrecoverable}
          className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
          {loading
            ? t('mesCredits.processing')
            : t('mesCredits.payBtn', {
                amount: amountTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 }),
              })}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MesCredits() {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')

  const [dashboard, setDashboard] = useState<CreditDashboard | null>(null)

  // Purchase flow state
  const [purchaseStep, setPurchaseStep] = useState<'idle' | 'amount' | 'payment' | 'success'>(
    'idle'
  )
  const [customAmount, setCustomAmount] = useState(50)
  const [clientSecret, setClientSecret] = useState('')
  const [publishableKey, setPublishableKey] = useState('')
  const [newBalance, setNewBalance] = useState<number | null>(null)
  const [purchaseError, setPurchaseError] = useState('')
  const [purchaseLoading, setPurchaseLoading] = useState(false)
  const [purchaseAmountHT, setPurchaseAmountHT] = useState(0)
  const [purchaseVatRate, setPurchaseVatRate] = useState(20)
  const [purchaseAmountTTC, setPurchaseAmountTTC] = useState(0)

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  )

  const load = async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    setError('')
    try {
      const data = await api.getCreditsDashboard()
      setDashboard(data)
      setPublishableKey(data.publishable_key ?? '')
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(getApiErrorKey(apiErr, 'mesCredits.loadError'))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleInitiatePurchase = async () => {
    setPurchaseError('')
    setPurchaseLoading(true)
    try {
      const result = await api.initiateCreditsPurchase(customAmount)
      setClientSecret(result.client_secret)
      setPublishableKey(result.publishable_key)
      setPurchaseAmountHT(result.amount_ht ?? customAmount)
      setPurchaseVatRate(result.vat_rate ?? 20)
      setPurchaseAmountTTC(result.amount_ttc ?? customAmount)
      setPurchaseStep('payment')
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setPurchaseError(getApiErrorKey(apiErr, 'mesCredits.initError'))
    } finally {
      setPurchaseLoading(false)
    }
  }

  const handlePaymentSuccess = (balance: number) => {
    setNewBalance(balance)
    setPurchaseStep('success')
    load(true)
  }

  const handleCancelPurchase = () => {
    setPurchaseStep('idle')
    setClientSecret('')
    setPurchaseError('')
    setPurchaseAmountHT(0)
    setPurchaseVatRate(20)
    setPurchaseAmountTTC(0)
  }

  // ── render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-blue-500" size={36} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-600 dark:text-red-400">
        <AlertTriangle size={32} className="mx-auto mb-2" />
        <p>{t(error)}</p>
      </div>
    )
  }

  const pool = dashboard?.pool ?? {}
  const quotaStatus = dashboard?.quota_status ?? null
  const transactions: CreditTransaction[] = dashboard?.transactions ?? []
  const threshold20 = dashboard?.threshold_20pct ?? 0
  const available = pool.available_credits ?? 0
  const isLow = threshold20 > 0 && available <= threshold20

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <CreditCard size={22} className="text-blue-500" />
          {t('mesCredits.title')}
        </h1>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {t('mesCredits.refresh')}
        </button>
      </div>

      {/* Balance card */}
      <div
        className={`rounded-xl p-5 ${isLow ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-600' : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">
              {t('mesCredits.balance')}
            </p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {available.toLocaleString('fr-FR')}{' '}
              <span className="text-xl font-semibold text-gray-500 dark:text-gray-400">
                {t('mesCredits.creditsUnit')}
              </span>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {t('mesCredits.balanceHint')} <strong>{t('mesCredits.creditsRule')}</strong>
            </p>
            {isLow && (
              <p className="mt-2 text-sm text-amber-700 dark:text-amber-300 flex items-center gap-1">
                <AlertTriangle size={14} /> {t('mesCredits.lowBalance')}
              </p>
            )}
          </div>
          {purchaseStep === 'idle' && (
            <button
              onClick={() => setPurchaseStep('amount')}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap"
            >
              <CreditCard size={16} />
              {t('mesCredits.recharge')}
            </button>
          )}
        </div>
      </div>

      {/* Purchase flow — step: choose amount */}
      {purchaseStep === 'amount' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {t('mesCredits.chooseAmount')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {[10, 20, 50, 100, 200].map((v) => (
              <button
                key={v}
                onClick={() => setCustomAmount(v)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${customAmount === v ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400'}`}
              >
                {v} €
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
              {t('mesCredits.customAmount')}
            </label>
            <div className="relative">
              <input
                type="number"
                min={10}
                step={1}
                value={customAmount}
                onChange={(e) => setCustomAmount(Math.max(10, Number(e.target.value)))}
                className="w-28 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-1.5 text-sm pr-7"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                €
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-400">{t('mesCredits.amountHint')}</p>
          {purchaseError && (
            <p className="text-sm text-red-600 dark:text-red-400">{t(purchaseError)}</p>
          )}
          <div className="flex gap-3 pt-1">
            <button
              onClick={handleInitiatePurchase}
              disabled={purchaseLoading || customAmount < 10}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors"
            >
              {purchaseLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CreditCard size={16} />
              )}
              {purchaseLoading
                ? t('mesCredits.loading')
                : t('mesCredits.continue', { amount: customAmount })}
            </button>
            <button
              onClick={handleCancelPurchase}
              className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Purchase flow — step: Stripe payment */}
      {purchaseStep === 'payment' && clientSecret && stripePromise && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {t('mesCredits.securePayment')}
          </h2>
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, appearance: { theme: 'stripe' } }}
          >
            <PaymentFormInner
              amountHT={purchaseAmountHT}
              vatRate={purchaseVatRate}
              amountTTC={purchaseAmountTTC}
              onSuccess={handlePaymentSuccess}
              onCancel={handleCancelPurchase}
            />
          </Elements>
        </div>
      )}

      {/* Purchase flow — step: success */}
      {purchaseStep === 'success' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-xl p-5 flex items-center gap-4">
          <CheckCircle size={32} className="text-green-600 dark:text-green-400 shrink-0" />
          <div>
            <p className="font-semibold text-green-800 dark:text-green-300">
              {t('mesCredits.paymentSuccess')}
            </p>
            <p className="text-sm text-green-700 dark:text-green-400">
              {t('mesCredits.paymentSuccessMsg')}{' '}
              <strong>
                {(newBalance ?? 0).toLocaleString('fr-FR')} {t('mesCredits.creditsUnit')}
              </strong>
            </p>
            <button
              onClick={() => setPurchaseStep('idle')}
              className="mt-2 text-sm text-green-700 dark:text-green-400 underline"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Quota status */}
      {quotaStatus && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-blue-500" />
            {t('mesCredits.quotaTitle')}
            <span className="text-xs font-normal text-gray-400">({quotaStatus.plan_name})</span>
          </h2>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            <QuotaBar
              label={t('mesCredits.esignatures')}
              icon={<FileText size={14} />}
              unlimited={t('mesCredits.unlimited')}
              used={quotaStatus.usage?.esignature ?? 0}
              max={quotaStatus.quotas?.max_signatures ?? 0}
            />
            <QuotaBar
              label={t('mesCredits.emails')}
              icon={<Mail size={14} />}
              unlimited={t('mesCredits.unlimited')}
              used={quotaStatus.usage?.email ?? 0}
              max={quotaStatus.quotas?.max_emails ?? 0}
            />
            <QuotaBar
              label={t('mesCredits.aiTokens')}
              icon={<Mic size={14} />}
              unlimited={t('mesCredits.unlimited')}
              used={quotaStatus.usage?.ai_tokens ?? 0}
              max={quotaStatus.quotas?.max_ai_tokens ?? 0}
            />
          </div>
          {(quotaStatus.warnings ?? []).length > 0 && (
            <div className="mt-3 flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
              <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t('mesCredits.quotaWarning', {
                  resources: (quotaStatus.warnings as string[]).join(', '),
                })}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Recent transactions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Zap size={18} className="text-blue-500" />
          {t('mesCredits.historyTitle')}
        </h2>
        {transactions.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">{t('mesCredits.noTransactions')}</p>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 sm:hidden -mx-5">
              {transactions.map((tx) => (
                <li key={tx.id} className="px-5 py-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                      {tx.description}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <span
                    className={`flex-shrink-0 text-sm font-semibold tabular-nums ${tx.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}
                  >
                    {tx.amount > 0 ? '+' : ''}
                    {Number(tx.amount).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="text-left pb-2 font-medium">{t('common.date')}</th>
                    <th className="text-left pb-2 font-medium">{t('mesCredits.colDescription')}</th>
                    <th className="text-right pb-2 font-medium">{t('mesCredits.colCredits')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {new Date(tx.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-2 text-gray-700 dark:text-gray-300">{tx.description}</td>
                      <td
                        className={`py-2 text-right font-medium tabular-nums ${tx.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}
                      >
                        {tx.amount > 0 ? '+' : ''}
                        {Number(tx.amount).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
